import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';

import {useEffect, useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  isTerminalAvailabilityStatus,
  patchVehicleAvailabilityInCaches,
  sourcingApi,
} from '@dms/api/core';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

import {
  AVAILABILITY_CHECK_MAX_ATTEMPTS,
  AVAILABILITY_CHECK_POLLING_INTERVAL_MS,
} from '../constants/sourcingAvailabilityCheck';
import {SourcingVehicleAvailabilityUiStatus} from '../types/SourcingVehicleAvailabilityUiStatus';

type SourcingAvailabilityApiState = Parameters<
  ReturnType<typeof sourcingApi.endpoints.sourcingAvailabilityByAdId.select>
>[0];

type UseSourcingVehicleAvailabilityProps = {
  adId: string | Nullish;
  enabled: boolean;
};

type ResolvedAvailabilityStatus = {
  adId: string;
  status: 'active' | 'just_sold' | 'unsupported_platform';
};

/**
 * Detail live-availability poller + UI status.
 *
 * If listing already resolved this adId (terminal `sourcingAvailabilityByAdId`), we do **not**
 * re-check the network — we reuse that status for Flags and re-apply `justSold` onto the detail
 * cache so grey-out / Sold / hide-actions stay in sync without another availability-check call.
 */
export function useSourcingVehicleAvailability(props: UseSourcingVehicleAvailabilityProps): {
  status: SourcingVehicleAvailabilityUiStatus | null;
} {
  const dispatch =
    useDispatch<ThunkDispatch<SourcingAvailabilityApiState, unknown, UnknownAction>>();
  const store = useStore<SourcingAvailabilityApiState>();
  const [stoppedForAdId, setStoppedForAdId] = useState<string | null>(null);
  const [resolvedStatus, setResolvedStatus] = useState<ResolvedAvailabilityStatus | null>(null);

  const adId = props.adId;
  const availabilityCache = useSelector(
    sourcingApi.endpoints.sourcingAvailabilityByAdId.select({adId: adId ?? ''})
  );
  const cachedStatus = isNotNil(adId) ? availabilityCache.data?.status : undefined;

  // Capture terminal status during render so tab focus / brief cache GC cannot flash loading.
  // React "adjusting state while rendering" pattern (anti-flash).
  if (
    isNotNil(adId) &&
    isTerminalAvailabilityStatus(cachedStatus) &&
    (resolvedStatus?.adId !== adId || resolvedStatus.status !== cachedStatus)
  ) {
    setResolvedStatus({adId, status: cachedStatus});
  }

  const localTerminalStatus =
    isNotNil(adId) && resolvedStatus?.adId === adId ? resolvedStatus.status : null;
  const terminalStatus =
    localTerminalStatus ?? (isTerminalAvailabilityStatus(cachedStatus) ? cachedStatus : null);
  const isTerminal = isNotNil(terminalStatus);
  const isStopped = isNotNil(adId) && stoppedForAdId === adId;

  // Skip network poll when ByAdId is already terminal (e.g. resolved on the listing).
  const shouldPoll = props.enabled && isNotNil(adId) && !isStopped && !isTerminal;

  // Keep a subscription so RTK does not GC the per-adId upsert while the detail is open.
  // useEffect: subscription lifecycle. No approved-pattern equivalent.
  useEffect(
    function subscribeAvailabilityByAdIdCache() {
      if (isNil(adId) || !props.enabled) {
        return;
      }

      const subscription = dispatch(
        sourcingApi.endpoints.sourcingAvailabilityByAdId.initiate(
          {adId},
          {subscribe: true, forceRefetch: false}
        )
      );

      return () => {
        subscription.unsubscribe();
      };
    },
    [adId, props.enabled, dispatch]
  );

  // List may have terminalized ByAdId before detail was in cache. Re-apply justSold onto
  // whatever vehicle caches exist now (including detail) — no network check.
  // useEffect: sync after detail/list cache presence changes. No approved-pattern equivalent.
  useEffect(
    function syncJustSoldFromTerminalByAdId() {
      if (!props.enabled || isNil(adId) || isNil(terminalStatus)) {
        return;
      }

      dispatch((innerDispatch, getState) => {
        patchVehicleAvailabilityInCaches(adId, terminalStatus, {
          dispatch: innerDispatch,
          getState,
        });
      });
    },
    [props.enabled, adId, terminalStatus, dispatch]
  );

  // useEffect: interval poll with stop-on-terminal/error/cap + visibility resume.
  // No approved-pattern equivalent (same rationale as useSourcingIntervalPoller).
  useEffect(
    function pollAvailabilityCheck() {
      if (!shouldPoll || isNil(adId)) {
        return;
      }

      const polledAdId = adId;
      let cancelled = false;
      let inFlight = false;
      let attempts = 0;
      let intervalId: number | undefined;

      async function doPoll() {
        if (cancelled || inFlight || document.hidden) {
          return;
        }

        const cached = sourcingApi.endpoints.sourcingAvailabilityByAdId.select({adId: polledAdId})(
          store.getState()
        );
        // Terminal may land from a concurrent list poll before React re-renders shouldPoll.
        if (isTerminalAvailabilityStatus(cached.data?.status)) {
          return;
        }

        if (attempts >= AVAILABILITY_CHECK_MAX_ATTEMPTS) {
          setStoppedForAdId(polledAdId);
          return;
        }

        inFlight = true;
        attempts += 1;
        const subscription = dispatch(
          sourcingApi.endpoints.sourcingAvailabilityCheck.initiate(
            {adIds: [polledAdId]},
            {forceRefetch: true}
          )
        );

        try {
          await subscription.unwrap().catch((error: ApiError) => {
            if (!cancelled) {
              handleApiError(error);
              setStoppedForAdId(polledAdId);
            }
          });
        } finally {
          subscription.unsubscribe();
          inFlight = false;
        }
      }

      function startInterval() {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
        intervalId = window.setInterval(() => {
          void doPoll();
        }, AVAILABILITY_CHECK_POLLING_INTERVAL_MS);
      }

      function onVisibilityChange() {
        if (!document.hidden) {
          void doPoll();
          startInterval();
        }
      }

      void doPoll();
      startInterval();
      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        cancelled = true;
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    },
    [shouldPoll, adId, dispatch, store]
  );

  if (!props.enabled || isStopped) {
    return {status: null};
  }

  // Unsupported platform: stop polling, keep live availability unknown (no Flags).
  if (terminalStatus === 'unsupported_platform') {
    return {status: null};
  }

  if (isTerminal) {
    return {status: terminalStatus};
  }

  return {status: 'checking'};
}
